"use client";

import { useState } from "react";
import { flushSync } from "react-dom";
import { CopyButton } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import type { CRYPTOGRAM_GENERATOR_STRINGS } from "@/components/tools/cryptogram-generator";
import {
  CryptogramGrid,
  FrequencyTable,
  KeyTable,
} from "@/components/tools/games-organizers/cryptogram-sheet";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Cryptogram,
  cipherLetterFor,
  cipherText,
} from "@/lib/tool-kit/games-organizers/cryptogram";
import { formatSeed } from "@/lib/tool-kit/worksheets/random";

type Strings = typeof CRYPTOGRAM_GENERATOR_STRINGS;
type PrintTarget = "puzzle" | "both";

const SHEET = "space-y-4 text-foreground print:text-black";
const SHEET_H2 = "font-semibold text-xl";

function NameLine({ strings }: { strings: Strings }) {
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
      <span className="flex items-end gap-2">
        {strings.name}
        <span className="inline-block w-56 border-current border-b" />
      </span>
      <span className="flex items-end gap-2">
        {strings.date}
        <span className="inline-block w-32 border-current border-b" />
      </span>
    </div>
  );
}

/** The cryptogram sheet, its answer key, and the print and copy controls. */
export default function CryptogramOutput({
  puzzle,
  seed,
  author,
  origin,
  showAuthor,
  showFrequency,
  nameLine,
  strings,
}: {
  puzzle: Cryptogram;
  seed: number;
  author: string;
  origin: string;
  showAuthor: boolean;
  showFrequency: boolean;
  nameLine: boolean;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [showKey, setShowKey] = useState(false);
  const [printTarget, setPrintTarget] = useState<PrintTarget>("puzzle");

  function print(target: PrintTarget) {
    flushSync(() => setPrintTarget(target));
    window.print();
  }

  const pairs = puzzle.givenLetters
    .map((letter) =>
      t(strings.givenPair, {
        cipher: cipherLetterFor(puzzle.key, letter),
        plain: letter,
      })
    )
    .join(", ");
  const byline = author ? t(strings.byline, { author }) : "";
  const keyByline = origin
    ? t(strings.keyAuthor, { author, source: origin })
    : byline;
  const keyPrint = printTarget === "both" ? "print:block" : "print:hidden";

  return (
    <>
      <div className="space-y-3 print:hidden">
        <div aria-live="polite" className="space-y-1">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <p className={HINT}>
            {t(strings.puzzleNumber, { seed: formatSeed(seed) })} ·{" "}
            {t(strings.statsLine, {
              letters: fmt(puzzle.letters),
              distinct: fmt(puzzle.distinct),
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => print("puzzle")}
            type="button"
          >
            {strings.printPuzzle}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => print("both")}
            type="button"
          >
            {strings.printBoth}
          </button>
          <button
            aria-pressed={showKey}
            className={BUTTON_SECONDARY}
            onClick={() => setShowKey((on) => !on)}
            type="button"
          >
            {showKey ? strings.hideKey : strings.showKey}
          </button>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyCipher}
            text={cipherText(puzzle)}
          />
        </div>
      </div>

      <section
        className={`rounded-lg border border-border p-5 print:border-0 print:p-0 ${SHEET}`}
      >
        <h2 className={SHEET_H2}>{strings.sheetTitle}</h2>
        {nameLine ? <NameLine strings={strings} /> : null}
        <p className="text-sm">{strings.instructions}</p>
        {pairs ? (
          <p className="font-medium text-sm">
            {t(strings.givenIntro, { pairs })}
          </p>
        ) : null}
        <CryptogramGrid
          answers={false}
          label={strings.gridLabel}
          puzzle={puzzle}
        />
        {showAuthor && byline ? (
          <p className="text-end text-sm">{byline}</p>
        ) : null}
        {showFrequency ? (
          <div className="space-y-1">
            <p className="font-medium text-sm">{strings.frequencyHeading}</p>
            <FrequencyTable
              counts={puzzle.counts}
              label={strings.frequencyLabel}
            />
          </div>
        ) : null}
      </section>

      <section
        className={`rounded-lg border border-border p-5 ${showKey ? "" : "hidden"} ${keyPrint} print:break-before-page print:border-0 print:p-0 ${SHEET}`}
      >
        <h2 className={SHEET_H2}>{strings.keyTitle}</h2>
        <CryptogramGrid answers label={strings.keyGridLabel} puzzle={puzzle} />
        {author ? <p className="text-end text-sm">{keyByline}</p> : null}
        <div className="space-y-1">
          <p className="font-medium text-sm">{strings.keyTableHeading}</p>
          <KeyTable
            cipherLabel={strings.cipherRow}
            plainLabel={strings.plainRow}
            puzzle={puzzle}
          />
        </div>
      </section>
    </>
  );
}
